import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthenticatedApiContext } from '../api/useApiClient';
import { ConsoleAccessStatus } from '../types';
import { useAsync } from './useAsync';
import { useAsyncEffect } from './useAsyncEffect';
import { useIsMounted } from './useIsMounted';

export const CONSOLE_ACCESS_POLL_INTERVAL_MS = 30_000;

interface UseConsoleAccessStatusesParams {
  enabled: boolean;
  /** Changes whenever the set of Active cloud instances changes, so the statuses are fetched again. */
  instanceIdsKey?: string;
}

interface ConsoleAccessStatusesResult {
  getConsoleAccess: ({ instanceId }: { instanceId: string }) => ConsoleAccessStatus;
}

/** Console access status per instance; polls while a row is NotConfigured and reloads on focus. */
export const useConsoleAccessStatuses = ({
  enabled,
  instanceIdsKey = '',
}: UseConsoleAccessStatusesParams): ConsoleAccessStatusesResult => {
  const apiClient = useAuthenticatedApiContext();
  const isMounted = useIsMounted();
  const [statuses, setStatuses] = useState<Map<string, ConsoleAccessStatus>>(new Map());

  const loadStatuses = useAsync(
    useCallback(async (): Promise<void> => {
      if (!enabled) {
        setStatuses(new Map());
        return;
      }
      try {
        const entries = await apiClient.getConsoleAccessStatuses();
        if (isMounted()) {
          setStatuses(new Map(entries.map(({ instanceId, consoleAccess }) => [instanceId, consoleAccess])));
        }
      } catch {
        // Keep the previous statuses: a failed lookup must not change what the table shows.
      }
    }, [apiClient, enabled, isMounted]),
    { autoReset: true }
  );
  const { execute: reloadStatuses } = loadStatuses;

  useAsyncEffect(loadStatuses, [enabled, instanceIdsKey]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const reloadWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        reloadStatuses();
      }
    };
    const reloadOnFocus = () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      reloadStatuses();
    };
    window.addEventListener('focus', reloadOnFocus);
    document.addEventListener('visibilitychange', reloadWhenVisible);
    return () => {
      window.removeEventListener('focus', reloadOnFocus);
      document.removeEventListener('visibilitychange', reloadWhenVisible);
    };
  }, [enabled, reloadStatuses]);

  const hasNotConfigured = useMemo(
    () => Array.from(statuses.values()).some((status) => status === ConsoleAccessStatus.NotConfigured),
    [statuses]
  );

  useEffect(() => {
    if (!enabled || !hasNotConfigured) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      // A hidden tab reloads when it becomes visible again, so polling it only burns requests.
      if (document.visibilityState !== 'visible') {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      reloadStatuses();
    }, CONSOLE_ACCESS_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [enabled, hasNotConfigured, reloadStatuses]);

  const getConsoleAccess = useCallback(
    ({ instanceId }: { instanceId: string }): ConsoleAccessStatus =>
      statuses.get(instanceId) ?? ConsoleAccessStatus.Unknown,
    [statuses]
  );

  return { getConsoleAccess };
};
